import React, { useState, useCallback, useMemo, useContext, useRef } from "react";
import {
  BackLink,
  Title,
  Text,
  Field,
  TextField,
  Button,
  Autocomplete,
  DropdownPaginated,
  DateTimePickerPopup,
  Pill,
} from "components/commons";
import Skeleton from "@material-ui/lab/Skeleton";
import { prettifyProductCodeStyle } from "utils/pretty.utils";
import { ConfirmModal, SuccessModal } from "components/modals";
import { useModal, useRouter, useApi, useMount } from "hooks";
import locale from "localization";
import { Path, PriceBuildUpStatus } from "enums";
import classNames from "classnames";
import UserContext from "contexts/user.context";
import { getDepots, getPriceBuildUp, getPriceBuildUps, updatePriceBuildUp, getMovements } from "apis";
import moment from "moment";
import { CircularProgress } from "@mui/material";
import styles from "./price-build-up-edit.module.scss";

// ─── Constants ────────────────────────────────────────────────────────────────

const PRODUCTS = [
  { name: "Extreme 105", code: "gas105" },
  { name: "Extreme 97", code: "gas97" },
  { name: "Extreme 95", code: "gas95" },
  { name: "Extreme U", code: "gas91" },
  { name: "Diesel", code: "diesel" },
  { name: "Kerosene", code: "kerosene" },
  { name: "LPG", code: "lpg" },
];

const SUBSIDY_OPTIONS = [
  { label: "LBAM", value: "LBAM" },
  { label: "PBC", value: "PBC" },
  { label: "PAM", value: "PAM" },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const parseNum = (val) => parseFloat(val) || 0;
const fmt2 = (val) => parseNum(val).toFixed(2);
const fmt4 = (val) => parseNum(val).toFixed(4);

const isNumericPeriod = (val, decimals = 2) => {
  if (val === "" || val === undefined) return true;
  return new RegExp(`^[0-9]*\\.?[0-9]{0,${decimals}}$`).test(val);
};

const isNumericPeriodMinus = (val, decimals = 4) => {
  if (val === "" || val === undefined) return true;
  return new RegExp(`^-?[0-9]*\\.?[0-9]{0,${decimals}}$`).test(val);
};

const createInitialRow = (product) => ({
  productName: product.name,
  productCode: product.code,
  depot: "",
  depotLabel: "",
  freight: "",
  pumpPrice: "",
  previousPumpPrice: "",
  previousPumpPriceDisabled: false,
  stationUm: "",
  wppAdjustment: "",
  wppAdjustmentDisabled: false,
});

const computeWpp = (row, subsidyRowsArg) => {
  const totalSubs = subsidyRowsArg.reduce(
    (sum, sr) => sum + parseFloat(sr.amounts[row.productCode] || 0),
    0,
  );
  return (
    parseFloat(row.pumpPrice || 0) -
    parseFloat(row.freight || 0) -
    totalSubs -
    parseFloat(row.stationUm || 0)
  );
};

const createInitialSubsidyAmounts = () =>
  PRODUCTS.reduce((acc, p) => ({ ...acc, [p.code]: "" }), {});

// ─── Sub-components ───────────────────────────────────────────────────────────

const StatusPill = ({ status }) => {
  const upper = (status || "").toUpperCase();
  if (upper === "SCHEDULED") return <Pill cheddar>{locale.scheduled}</Pill>;
  if (upper === "ACTIVE") return <Pill grass>{locale.active}</Pill>;
  if (upper === "EXPIRED") return <Pill cement>{locale.expired}</Pill>;
  if (upper === "CANCELLED") return <Pill deepRed>{locale.cancelled}</Pill>;
  return <Text>{status}</Text>;
};

const StationDetails = ({ details }) => (
  <div className={styles.stationDetails}>
    <div className={styles.stationDetailsGrid}>
      <div className={styles.stationDetailItem}>
        <Text className={styles.detailLabel}>{locale.stationName}</Text>
        <Text className={styles.detailValue}>{details.name}</Text>
      </div>
      <div className={styles.stationDetailItem}>
        <Text className={styles.detailLabel}>{locale.customerName}</Text>
        <Text className={styles.detailValue}>{details.businessName || details.customerName}</Text>
      </div>
      <div className={styles.stationDetailItem}>
        <Text className={styles.detailLabel}>{locale.stationType}</Text>
        <Text className={styles.detailValue}>{details.stationType}</Text>
      </div>
      <div className={styles.stationDetailItem}>
        <Text className={styles.detailLabel}>{locale.island}</Text>
        <Text className={styles.detailValue}>{details.island?.toUpperCase()}</Text>
      </div>
      <div className={styles.stationDetailItem}>
        <Text className={styles.detailLabel}>{locale.area}</Text>
        <Text className={styles.detailValue}>{details.province || details.area}</Text>
      </div>
    </div>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const PriceBuildUpEditModule = () => {
  const { history, location } = useRouter();
  const { user } = useContext(UserContext);
  const confirmModal = useModal();
  const successModal = useModal();

  const priceBuildUpId = location?.state?.priceBuildUpId;

  // ── State ──
  const [loading, setLoading] = useState(true);
  const [priceBuildUp, setPriceBuildUp] = useState(null);
  const [stationDetails, setStationDetails] = useState(null);
  const [effectiveDateTime, setEffectiveDateTime] = useState(null);
  const [effectiveDateConstraint, setEffectiveDateConstraint] = useState(null);
  const [productRows, setProductRows] = useState(PRODUCTS.map(createInitialRow));
  const [subsidyRows, setSubsidyRows] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [saveAttempted, setSaveAttempted] = useState(false);
  const [conflictStatus, setConflictStatus] = useState(null);
  const [movementConflict, setMovementConflict] = useState(false);
  const [isCheckingConflict, setIsCheckingConflict] = useState(false);
  const [originalEffectiveAt, setOriginalEffectiveAt] = useState(null);
  const [allDepotsData, setAllDepotsData] = useState([]);
  const initialDataRef = useRef(null);
  const conflictCheckRef = useRef(0);

  // ── APIs ──
  const { request: fetchPriceBuildUp } = useApi({ api: getPriceBuildUp, handleOwnError: true });
  const { request: fetchAllDepots } = useApi({
    api: getDepots,
    handleOwnError: true,
    params: {
      page: 1,
      perPage: 9999,
      orderBy: "name",
    },
  });
  const { request: submitUpdate } = useApi({ api: updatePriceBuildUp, handleOwnError: true });
  const { request: fetchAdjacentPbu } = useApi({ api: getPriceBuildUps, handleOwnError: true });
  const { request: checkConflict } = useApi({ api: getPriceBuildUps, handleOwnError: true });
  const { request: fetchPrevPbu } = useApi({ api: getPriceBuildUps, handleOwnError: true });
  const { request: checkMovementConflict } = useApi({ api: getMovements, handleOwnError: true });

  // ── Load data on mount ──
  useMount(async () => {
    if (!priceBuildUpId) {
      history.replace(Path.PriceBuildUpList);
      return;
    }
    try {
      const [detail, depotsRes] = await Promise.all([
        fetchPriceBuildUp({ priceBuildUpId }),
        fetchAllDepots({ page: 1, perPage: 9999 }),
      ]);

      if (!detail) return;
      const detailStatus = (detail.status || "").toUpperCase();
      if (detailStatus === "CANCELLED") {
        history.replace(Path.PriceBuildUpList);
        return;
      }
      const prev = detail.prevPriceBuildup ?? null;
      const prevDetailMap = (prev?.priceBuildupDetails || []).reduce((acc, pd) => {
        acc[pd.productCode] = pd;
        return acc;
      }, {});

      setPriceBuildUp(detail);
      setStationDetails(detail.station);
      const initEffectiveDateTime = detail.effectiveAt ? new Date(detail.effectiveAt) : new Date();
      setEffectiveDateTime(initEffectiveDateTime);
      setOriginalEffectiveAt(initEffectiveDateTime);

      const stationCode = detail.station?.stationCode;
      if (detailStatus === "EXPIRED") {
        const res = await fetchAdjacentPbu({
          page: 1,
          perPage: 1,
          searchKey: stationCode,
          searchType: "stationCode",
          statuses: PriceBuildUpStatus.Active,
        });
        const adjacent = res?.priceBuildUps?.[0];
        if (adjacent?.effectiveAt) setEffectiveDateConstraint(new Date(adjacent.effectiveAt));
      } else if (detailStatus === "ACTIVE") {
        const res = await fetchAdjacentPbu({
          page: 1,
          perPage: 1,
          searchKey: stationCode,
          searchType: "stationCode",
          statuses: PriceBuildUpStatus.Expired,
          orderBy: "effectiveAt",
          order: "desc",
        });
        const adjacent = res?.priceBuildUps?.[0];
        if (adjacent?.effectiveAt) setEffectiveDateConstraint(new Date(adjacent.effectiveAt));
      }

      const details = detail.priceBuildupDetails || [];
      const allDepots = depotsRes?.depots || [];
      setAllDepotsData(allDepots);

      const stationProducts = PRODUCTS.filter((p) =>
        details.some((item) => item.productCode === p.code),
      );

      const initProductRows = stationProducts.map((product) => {
        const d = details.find((item) => item.productCode === product.code);
        return {
          productName: product.name,
          productCode: product.code,
          depot: d.depotId ? String(d.depotId) : "",
          depotLabel: allDepots.find((dep) => dep.depotId === d.depotId)?.depotCode || "",
          freight: d.freight != null ? fmt4(d.freight) : "",
          pumpPrice: d.pumpPrice != null ? fmt2(d.pumpPrice) : "",
          previousPumpPrice: d.oldPumpPrice != null ? fmt2(d.oldPumpPrice) : "",
          previousPumpPriceDisabled: !!prev,
          previousFinalWpp: d.oldFinalWpp
            ? fmt4(d.oldFinalWpp)
            : prevDetailMap[product.code]?.finalWpp != null
              ? fmt4(prevDetailMap[product.code].finalWpp)
              : "",
          previousFinalWppDisabled: !!prev,
          stationUm: d.stationUm != null ? fmt2(d.stationUm) : "",
          wppAdjustment: d.wppAdjustment != null ? fmt4(d.wppAdjustment) : "",
          wppAdjustmentDisabled: false,
        };
      });
      setProductRows(initProductRows);

      const initSubsidyRows = [];
      const subsidyKeys = ["LBAM", "PBC", "PAM"];
      const fieldMap = { LBAM: "lbam", PBC: "pbc", PAM: "pam" };
      subsidyKeys.forEach((key) => {
        const hasAnyValue = details.some((d) => d[fieldMap[key]] != null && d[fieldMap[key]] !== 0);
        if (!hasAnyValue) return;
        const amounts = stationProducts.reduce((acc, product) => {
          const d = details.find((item) => item.productCode === product.code);
          const val = d ? d[fieldMap[key]] : null;
          acc[product.code] = val != null ? fmt2(val) : "";
          return acc;
        }, {});
        initSubsidyRows.push({ name: key, amounts });
      });
      setSubsidyRows(initSubsidyRows);

      initialDataRef.current = {
        effectiveDateTime: initEffectiveDateTime,
        productRows: initProductRows,
        subsidyRows: initSubsidyRows,
      };
    } finally {
      setLoading(false);
    }
  });

  // ── Depot paginated request ──
  const depotRequest = useCallback(
    async ({ searchKey }) => {
      const filtered = searchKey
        ? allDepotsData.filter((d) => d.depotCode.toLowerCase().includes(searchKey.toLowerCase()))
        : allDepotsData;
      return { depots: filtered, count: filtered.length };
    },
    [allDepotsData],
  );

  // ── Row updater ──
  const updateRow = useCallback((index, patch) => {
    setProductRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }, []);

  // ── Depot change ──
  const handleDepotChange = useCallback(
    (index, { value, label }) => {
      updateRow(index, {
        depot: value || "",
        depotLabel: label || "",
        freight: "",
      });
    },
    [updateRow],
  );

  // ── Subsidy helpers ──
  const handleAddSubsidyWithType = useCallback((type) => {
    setSubsidyRows((prev) => [...prev, { name: type, amounts: createInitialSubsidyAmounts() }]);
  }, []);

  const handleRemoveSubsidy = useCallback((sri) => {
    setSubsidyRows((prev) => prev.filter((_, i) => i !== sri));
  }, []);

  const handleSubsidyNameChange = useCallback((sri, name) => {
    setSubsidyRows((prev) => prev.map((sr, i) => (i === sri ? { ...sr, name } : sr)));
  }, []);

  const handleSubsidyAmountChange = useCallback((sri, productCode, amount) => {
    setSubsidyRows((prev) =>
      prev.map((sr, i) =>
        i === sri ? { ...sr, amounts: { ...sr.amounts, [productCode]: amount } } : sr,
      ),
    );
  }, []);

  const handleEffectiveDateTimeChange = useCallback(
    async (_, { value }) => {
      setEffectiveDateTime(value);
      setConflictStatus(null);
      setMovementConflict(false);

      if (!value || !priceBuildUp) return;

      if (
        originalEffectiveAt &&
        moment(value).startOf("minute").isSame(moment(originalEffectiveAt).startOf("minute"))
      ) {
        return;
      }

      const checkId = ++conflictCheckRef.current;
      const stationCode = priceBuildUp?.station?.stationCode;
      const startEffectiveAt = moment(value).startOf("minute").toISOString();
      const endEffectiveAt = moment(value).endOf("minute").toISOString();
      const allStatuses = [
        PriceBuildUpStatus.Active,
        PriceBuildUpStatus.Scheduled,
        PriceBuildUpStatus.Expired,
      ].join(",");
      setIsCheckingConflict(true);
      try {
        const [res, prevRes] = await Promise.all([
          checkConflict({
            page: 1,
            perPage: 2,
            searchKey: stationCode,
            searchType: "stationCode",
            statuses: allStatuses,
            startDate: startEffectiveAt,
            endDate: endEffectiveAt,
          }),
          fetchPrevPbu({
            page: 1,
            perPage: 2,
            searchKey: stationCode,
            searchType: "stationCode",
            statuses: allStatuses,
            orderBy: "effectiveAt",
            order: "desc",
            endDate: moment(value).subtract(1, "second").toISOString(),
          }),
        ]);
        if (conflictCheckRef.current !== checkId) return;

        const conflicting = (res?.priceBuildUps || []).find(
          (pbu) => (pbu.priceBuildupId ?? pbu.priceBuildUpId) !== priceBuildUpId,
        );
        if (conflicting) {
          const rawStatus = conflicting.status || "";
          setConflictStatus(rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1).toLowerCase());
        }

        const prevPbuSummary = (prevRes?.priceBuildUps || []).find(
          (pbu) => (pbu.priceBuildupId ?? pbu.priceBuildUpId) !== priceBuildUpId,
        ) ?? null;
        const prevPbuId = prevPbuSummary
          ? (prevPbuSummary.priceBuildupId ?? prevPbuSummary.priceBuildUpId)
          : null;

        if (prevPbuId) {
          const prevDetail = await fetchPriceBuildUp({ priceBuildUpId: prevPbuId });
          if (conflictCheckRef.current !== checkId) return;
          const prevDetailMap = (prevDetail?.priceBuildupDetails || []).reduce((acc, pd) => {
            acc[pd.productCode] = pd;
            return acc;
          }, {});
          setProductRows((rows) =>
            rows.map((row) => ({
              ...row,
              previousPumpPrice:
                prevDetailMap[row.productCode]?.pumpPrice != null
                  ? fmt2(prevDetailMap[row.productCode].pumpPrice)
                  : "",
              previousFinalWpp:
                prevDetailMap[row.productCode]?.finalWpp != null
                  ? fmt4(prevDetailMap[row.productCode].finalWpp)
                  : "",
              previousPumpPriceDisabled: true,
              previousFinalWppDisabled: true,
            })),
          );
        } else {
          setProductRows((rows) =>
            rows.map((row) => ({
              ...row,
              previousPumpPrice: "",
              previousFinalWpp: "",
              previousPumpPriceDisabled: false,
              previousFinalWppDisabled: false,
            })),
          );
        }

        if (moment(value).isAfter(moment())) {
          const mvRes = await checkMovementConflict({
            page: 1,
            perPage: 1,
            dateRangeBy: "effectiveAt",
            startDate: startEffectiveAt,
            endDate: endEffectiveAt,
          });
          if (conflictCheckRef.current !== checkId) return;
          if (mvRes?.movements?.length > 0) {
            setMovementConflict(true);
          }
        }
      } catch {
      } finally {
        if (conflictCheckRef.current === checkId) setIsCheckingConflict(false);
      }
    },
    [priceBuildUp, priceBuildUpId, originalEffectiveAt, checkConflict, fetchPrevPbu, fetchPriceBuildUp, checkMovementConflict],
  );

  // ── Validation ──
  const hasValidProductRows = useMemo(() => {
    const hasPricingWithoutDepot = productRows.some(
      (row) => !row.depot && (row.freight || row.pumpPrice || row.previousPumpPrice || row.stationUm),
    );
    if (hasPricingWithoutDepot) return false;
    const rowsWithDepot = productRows.filter((row) => row.depot);
    if (rowsWithDepot.length === 0) return false;
    return rowsWithDepot.every(
      (row) =>
        row.freight &&
        row.pumpPrice &&
        (row.previousPumpPriceDisabled || row.previousPumpPrice) &&
        row.stationUm,
    );
  }, [productRows]);

  const isExpired = (priceBuildUp?.status || "").toUpperCase() === "EXPIRED";
  const isActive = (priceBuildUp?.status || "").toUpperCase() === "ACTIVE";
  const isScheduled = (priceBuildUp?.status || "").toUpperCase() === "SCHEDULED";

  const isFormValid =
    !!effectiveDateTime &&
    hasValidProductRows &&
    !conflictStatus &&
    !movementConflict &&
    !isCheckingConflict &&
    (!isScheduled || effectiveDateTime > new Date());

  const hasChanges = useMemo(() => {
    const init = initialDataRef.current;
    if (!init) return false;

    if ((effectiveDateTime?.getTime() ?? null) !== (init.effectiveDateTime?.getTime() ?? null))
      return true;

    const editableFields = [
      "depot",
      "freight",
      "pumpPrice",
      "previousPumpPrice",
      "previousFinalWpp",
      "stationUm",
      "wppAdjustment",
    ];
    for (let i = 0; i < productRows.length; i++) {
      for (const field of editableFields) {
        if ((productRows[i][field] || "") !== (init.productRows[i][field] || "")) return true;
      }
    }

    if (subsidyRows.length !== init.subsidyRows.length) return true;
    for (const curr of subsidyRows) {
      const orig = init.subsidyRows.find((s) => s.name === curr.name);
      if (!orig) return true;
      for (const { code } of PRODUCTS) {
        if ((curr.amounts[code] || "") !== (orig.amounts[code] || "")) return true;
      }
    }

    return false;
  }, [effectiveDateTime, productRows, subsidyRows]);

  const isDisabledAddSubsidy = useMemo(() => productRows.every((row) => !row.depot), [productRows]);

  // ── Save flow ──
  const handleSaveClick = useCallback(() => {
    if (!hasChanges) return;
    setSaveAttempted(true);
    if (!isFormValid) return;
    confirmModal.show();
  }, [hasChanges, isFormValid, confirmModal]);

  const handleConfirmSave = useCallback(async () => {
    setIsSaving(true);
    try {
      const payload = {
        priceBuildUpId,
        stationId: priceBuildUp?.station?.stationId || priceBuildUp?.stationId,
        effectiveAt: effectiveDateTime,
        createdBy: user?.fullName || user?.name || user?.email,
        createdByEmail: user?.email,
        priceBuildupDetails: productRows
          .filter((row) => row.depot)
          .map((row) => {
            const lbam = parseNum(
              subsidyRows.find((s) => s.name === "LBAM")?.amounts[row.productCode] || 0,
            );
            const pbc = parseNum(
              subsidyRows.find((s) => s.name === "PBC")?.amounts[row.productCode] || 0,
            );
            const pam = parseNum(
              subsidyRows.find((s) => s.name === "PAM")?.amounts[row.productCode] || 0,
            );
            const totalSubsidies = subsidyRows.reduce(
              (sum, sr) => sum + parseNum(sr.amounts[row.productCode] || 0),
              0,
            );
            const wpp = computeWpp(row, subsidyRows);
            const wppAdjNum = parseNum(row.wppAdjustment);
            return {
              productCode: row.productCode,
              depotId: Number(row.depot),
              freight: parseNum(row.freight),
              pumpPrice: parseNum(row.pumpPrice),
              adjustment: parseNum(row.pumpPrice) - parseNum(row.previousPumpPrice),
              oldPumpPrice: parseNum(row.previousPumpPrice),
              oldFinalWpp: parseNum(row.previousFinalWpp),
              lbam,
              pbc,
              pam,
              totalSubsidies,
              stationUm: parseNum(row.stationUm),
              wpp,
              wppAdjustment: wppAdjNum,
              finalWpp: wpp + wppAdjNum,
            };
          }),
      };
      await submitUpdate(payload);
      confirmModal.close();
      successModal.show();
    } catch (err) {
      if (err?.data?.errorCode === "PB1003") {
        const msg = err?.data?.message || "";
        const match = msg.toLowerCase().match(/\b(active|scheduled|expired)\b/);
        const status = match ? match[1].charAt(0).toUpperCase() + match[1].slice(1) : "Active";
        confirmModal.close();
        setConflictStatus(status);
      }
    } finally {
      setIsSaving(false);
    }
  }, [
    priceBuildUpId,
    priceBuildUp,
    effectiveDateTime,
    user,
    productRows,
    subsidyRows,
    submitUpdate,
    confirmModal,
    successModal,
  ]);

  const handleSuccessGotIt = useCallback(() => {
    successModal.close();
    history.push(Path.PriceBuildUpList);
  }, [successModal, history]);

  const handleCancel = useCallback(() => history.goBack(), [history]);

  const showCellError = (row, value) => saveAttempted && !!row.depot && !value;

  // ── Subsidies computed ──
  const canAddMoreSubsidies = SUBSIDY_OPTIONS.some(
    (opt) => !subsidyRows.some((sr) => sr.name === opt.value),
  );
  const subsidiesRowSpan = subsidyRows.length + (canAddMoreSubsidies ? 1 : 0) + 1;

  // ── Loading state ──
  if (loading) {
    return (
      <div className={`${styles.container} ${styles.fadeIn}`}>
        <Skeleton variant="text" width={140} height={18} style={{ marginBottom: 24 }} />
        <div className={styles.header}>
          <Skeleton variant="text" width={260} height={40} />
          <div className={styles.headerActions}>
            <Skeleton variant="rect" width={90} height={36} style={{ borderRadius: 6 }} />
            <Skeleton variant="rect" width={70} height={36} style={{ borderRadius: 6 }} />
          </div>
        </div>
        <div className={styles.section}>
          <Skeleton
            variant="rect"
            width={220}
            height={36}
            style={{ borderRadius: 4, marginBottom: 16 }}
          />
          {[170, 210, 130, 150, 190].map((w, i) => (
            <div
              key={i}
              style={{ display: "flex", gap: 40, marginBottom: 12, alignItems: "center" }}
            >
              <Skeleton variant="text" width={200} height={18} />
              <Skeleton variant="text" width={w} height={18} />
            </div>
          ))}
        </div>
        <div className={styles.section}>
          <Skeleton variant="text" width={180} height={22} style={{ marginBottom: 20 }} />
          <Skeleton
            variant="rect"
            width={500}
            height={40}
            style={{ borderRadius: 4, marginBottom: 16 }}
          />
          <Skeleton
            variant="rect"
            width={180}
            height={28}
            style={{ borderRadius: 14, marginBottom: 20 }}
          />
          <Skeleton variant="rect" width="100%" height={420} style={{ borderRadius: 8 }} />
        </div>
      </div>
    );
  }

  // ── Render ──
  return (
    <div className={`${styles.container} ${styles.fadeIn}`}>
      <ConfirmModal
        {...confirmModal}
        title={locale.saveChangesQuestion}
        content={
          <Text style={{ color: "black", margin: "0px 100px" }}>
            {locale.editPriceBuildUpContent}
          </Text>
        }
        primary={{
          text: locale.saveChanges,
          onClick: handleConfirmSave,
          loading: isSaving,
        }}
        secondary={{ text: locale.continueEditing }}
      />
      <SuccessModal
        {...successModal}
        title={locale.success}
        content={locale.detailHaveBeenSuccessfullyUpdated}
        primary={{ text: "Got It", onClick: handleSuccessGotIt }}
      />

      <BackLink text={locale.priceBuildUp} path={Path.PriceBuildUpList} />

      <div className={styles.header}>
        <Title>{locale.editNewPriceBuildUp}</Title>
        <div className={styles.headerActions}>
          <Button outlined onClick={handleCancel} data-testid="pbu-edit-cancel-btn">
            {locale.cancel}
          </Button>
          <Button
            primary
            className={!hasChanges || !isFormValid ? styles.saveBtnInactive : undefined}
            onClick={handleSaveClick}
            data-testid="pbu-edit-save-btn"
          >
            {locale.save}
          </Button>
        </div>
      </div>

      <Text className={styles.sectionTitle}>{locale.stationDetails}</Text>

      {/* ── Station Code (read-only) + Status ── */}
      <div className={styles.section}>
        <Field label={locale.stationCode} className={styles.fieldRow} labelPosition="left">
          <TextField value={stationDetails?.stationCode || ""} disabled />
        </Field>
        {stationDetails && <StationDetails details={stationDetails} />}
      </div>

      {/* ── Price Build-Up Details ── */}
      <div className={styles.section}>
        <Text className={styles.sectionTitle}>{locale.priceBuildUpDetails}</Text>

        <Field
          label="Effective Date and Time"
          labelPosition="left"
          className={styles.dateField}
          error={!!(conflictStatus || movementConflict)}
          message={
            isCheckingConflict ? (
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <CircularProgress size={12} />
                <Text>Checking for conflicts...</Text>
              </span>
            ) : conflictStatus ? (
              <Text className={styles.dateError}>
                {"AEIOU".includes(conflictStatus?.[0]) ? "An" : "A"}{" "}
                <strong>{conflictStatus}</strong> price build-up already exists for this station at
                the selected effective time. Please choose a different effective time.
              </Text>
            ) : movementConflict ? (
              <Text className={styles.dateError}>
                A <strong>Scheduled</strong> price movement will be implemented at the selected
                effective date and time. Please choose a different effective date and time.
              </Text>
            ) : null
          }
        >
          <DateTimePickerPopup
            name="effectiveDateTime"
            value={effectiveDateTime}
            placeholder="Select date and time"
            onChange={handleEffectiveDateTimeChange}
            minDateTime={
              isActive
                ? effectiveDateConstraint
                  ? moment(effectiveDateConstraint).add(1, "minute").toDate()
                  : undefined
                : isExpired
                  ? undefined
                  : new Date()
            }
            maxDateTime={
              isExpired
                ? effectiveDateConstraint
                  ? moment(effectiveDateConstraint).subtract(1, "minute").toDate()
                  : undefined
                : isActive
                  ? new Date(priceBuildUp?.effectiveAt)
                  : undefined
            }
            actions={{ clear: true, cancel: true, ok: true }}
            className={styles.effectiveDateTime}
            error={!!(conflictStatus || movementConflict)}
          />
        </Field>

        <div className={styles.metaItem} style={{ marginBottom: "2em" }}>
          <Text className={styles.detailLabel}>{locale.status}</Text>
          <StatusPill status={priceBuildUp?.status} />
        </div>

        <div className={styles.metaItem}>
          <Text className={styles.detailLabel}>{locale.priceType}</Text>
          <Text className={styles.detailValue}>
            {{
              "build-up": locale.buildUp,
              "nationwide-movement": locale.nationwideMovement,
              "franchise-movement": locale.franchiseMovement,
            }[priceBuildUp.type] || locale.NA}
          </Text>
        </div>

        {/* Price table */}
        <div className={styles.tableWrapper}>
          <table className={styles.priceTable}>
            <thead>
              <tr>
                <th className={styles.groupHeader}></th>
                <th className={styles.fieldHeader}></th>
                {productRows.map((row) => (
                  <th key={row.productCode} className={styles.productHeader}>
                    <span
                      className={styles.productBadge}
                      style={prettifyProductCodeStyle(row.productCode)}
                    />
                    {row.productName}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* ── FREIGHT ── */}
              <tr>
                <td rowSpan={2} className={styles.groupCell}>
                  {locale.freight}
                </td>
                <td className={styles.fieldCell}>{locale.depot}</td>
                {productRows.map((row, i) => (
                  <td key={row.productCode}>
                    <DropdownPaginated
                      className={styles.depotSelect}
                      value={row.depot || ""}
                      label={row.depotLabel || ""}
                      request={depotRequest}
                      formatRequest={(res) =>
                        (res?.depots || []).map((d) => ({
                          label: d.depotCode,
                          value: String(d.depotId),
                        }))
                      }
                      total={allDepotsData.length}
                      placeholder="Select"
                      onChange={(_, opt) => handleDepotChange(i, opt)}
                    />
                  </td>
                ))}
              </tr>
              <tr>
                <td className={styles.fieldCell}>{locale.freight}</td>
                {productRows.map((row, i) => (
                  <td key={row.productCode}>
                    <TextField
                      className={styles.numInput}
                      value={row.freight}
                      placeholder="0.0000"
                      disabled={!row.depot}
                      error={showCellError(row, row.freight)}
                      onChange={(_, { value }) => {
                        if (isNumericPeriod(value, 4) && parseNum(value) <= 999.9999)
                          updateRow(i, { freight: value });
                      }}
                      onBlur={(_, { value }) => {
                        if (value) {
                          const n = Math.min(parseNum(value), 999.9999);
                          updateRow(i, { freight: fmt4(n) });
                        }
                      }}
                    />
                  </td>
                ))}
              </tr>

              {/* ── PUMP PRICE ── */}
              <tr>
                <td rowSpan={3} className={styles.groupCell}>
                  {locale.pumpPrice}
                </td>
                <td className={styles.fieldCell}>{locale.pumpPrice}</td>
                {productRows.map((row, i) => (
                  <td key={row.productCode}>
                    <TextField
                      className={styles.numInput}
                      value={row.pumpPrice}
                      placeholder="0.00"
                      disabled={!row.depot}
                      error={showCellError(row, row.pumpPrice)}
                      onChange={(_, { value }) => {
                        if (isNumericPeriod(value, 2) && parseNum(value) <= 999.99)
                          updateRow(i, { pumpPrice: value });
                      }}
                      onBlur={(_, { value }) => {
                        if (value) {
                          const n = Math.min(parseNum(value), 999.99);
                          updateRow(i, { pumpPrice: fmt2(n) });
                        }
                      }}
                    />
                  </td>
                ))}
              </tr>
              <tr>
                <td className={styles.fieldCell}>{locale.adjustment}</td>
                {productRows.map((row) => {
                  const adj = parseNum(row.pumpPrice) - parseNum(row.previousPumpPrice);
                  return (
                    <td
                      key={row.productCode}
                      style={{ backgroundColor: "#f6f6f6", paddingLeft: "27px" }}
                    >
                      <Text
                        className={classNames(styles.computed, {
                          [styles.positive]: row.depot && adj > 0,
                          [styles.negative]: row.depot && adj < 0,
                        })}
                      >
                        {row.depot ? fmt2(adj) : "0.00"}
                      </Text>
                    </td>
                  );
                })}
              </tr>
              <tr>
                <td className={styles.fieldCell}>{locale.previousPumpPrice}</td>
                {productRows.map((row, i) => (
                  <td
                    key={row.productCode}
                    style={{
                      backgroundColor:
                        !row.depot || row.previousPumpPriceDisabled ? "#f6f6f6" : undefined,
                    }}
                  >
                    <TextField
                      className={styles.numInput}
                      value={row.previousPumpPrice}
                      placeholder="0.00"
                      disabled={!row.depot || row.previousPumpPriceDisabled}
                      error={showCellError(row, row.previousPumpPrice)}
                      onChange={(_, { value }) => {
                        if (isNumericPeriod(value, 2) && parseNum(value) <= 999.99)
                          updateRow(i, { previousPumpPrice: value });
                      }}
                      onBlur={(_, { value }) => {
                        if (value) {
                          const n = Math.min(parseNum(value), 999.99);
                          updateRow(i, { previousPumpPrice: fmt2(n) });
                        }
                      }}
                    />
                  </td>
                ))}
              </tr>
              {/* ── SUBSIDIES ── each type is its own row ── */}
              {subsidyRows.map((subsRow, sri) => {
                const usedNames = subsidyRows.map((s) => s.name).filter(Boolean);
                const optsForRow = SUBSIDY_OPTIONS.filter(
                  (opt) => !usedNames.includes(opt.value) || opt.value === subsRow.name,
                );
                return (
                  <tr key={`sub-${sri}`}>
                    {sri === 0 && (
                      <td rowSpan={subsidiesRowSpan} className={styles.groupCell}>
                        {locale.subsidies}
                      </td>
                    )}
                    <td className={styles.fieldCell}>
                      <div className={styles.subsidyFieldCell}>
                        <Autocomplete
                          value={subsRow.name}
                          options={optsForRow}
                          className={styles.subsidyTypeSelect}
                          placeholder="Select"
                          onChange={(_, { value }) => handleSubsidyNameChange(sri, value)}
                        />
                        <button
                          type="button"
                          className={styles.removeSubsidy}
                          onClick={() => handleRemoveSubsidy(sri)}
                        >
                          ×
                        </button>
                      </div>
                    </td>
                    {productRows.map((row) => (
                      <td key={row.productCode}>
                        <TextField
                          className={styles.numInput}
                          value={subsRow.amounts[row.productCode] || ""}
                          placeholder="0.00"
                          disabled={!row.depot || !subsRow.name}
                          onChange={(_, { value }) => {
                            if (isNumericPeriod(value, 2) && parseNum(value) <= 999.99)
                              handleSubsidyAmountChange(sri, row.productCode, value);
                          }}
                          onBlur={(_, { value }) => {
                            if (value) {
                              const n = Math.min(parseNum(value), 999.99);
                              handleSubsidyAmountChange(sri, row.productCode, fmt2(n));
                            }
                          }}
                        />
                      </td>
                    ))}
                  </tr>
                );
              })}

              {/* Empty subsidy dropdown row — visible while types remain */}
              {canAddMoreSubsidies && (
                <tr>
                  {subsidyRows.length === 0 && (
                    <td rowSpan={subsidiesRowSpan} className={styles.groupCell}>
                      {locale.subsidies}
                    </td>
                  )}
                  <td className={styles.fieldCell}>
                    <Autocomplete
                      key={subsidyRows.length}
                      value={null}
                      options={SUBSIDY_OPTIONS.filter(
                        (opt) => !subsidyRows.some((sr) => sr.name === opt.value),
                      )}
                      className={styles.subsidyTypeSelect}
                      placeholder="Add Subsidy"
                      onChange={(_, { value }) => handleAddSubsidyWithType(value)}
                      disabled={isDisabledAddSubsidy}
                    />
                  </td>
                  {productRows.map((row) => (
                    <td key={row.productCode}>
                      <TextField className={styles.numInput} value="" placeholder="0.00" disabled />
                    </td>
                  ))}
                </tr>
              )}

              {/* Subsidies TOTAL row */}
              <tr>
                {!canAddMoreSubsidies && subsidyRows.length === 0 && (
                  <td className={styles.groupCell}>{locale.subsidies}</td>
                )}
                <td className={styles.fieldCell}>{locale.subsidiesTotal}</td>
                {productRows.map((row) => {
                  const total = subsidyRows.reduce(
                    (sum, sr) => sum + parseNum(sr.amounts[row.productCode] || 0),
                    0,
                  );
                  return (
                    <td
                      key={row.productCode}
                      style={{ backgroundColor: "#f6f6f6", paddingLeft: "27px" }}
                    >
                      <Text className={classNames(styles.computed, styles.totalCell)}>
                        {row.depot ? fmt2(total) : "0.00"}
                      </Text>
                    </td>
                  );
                })}
              </tr>

              {/* ── MARGINS ── */}
              <tr>
                <td className={styles.groupCell}>{locale.margins}</td>
                <td className={styles.fieldCell}>{locale.stationUm}</td>
                {productRows.map((row, i) => (
                  <td key={row.productCode}>
                    <TextField
                      className={styles.numInput}
                      value={row.stationUm}
                      placeholder="0.00"
                      disabled={!row.depot}
                      error={showCellError(row, row.stationUm)}
                      onChange={(_, { value }) => {
                        if (isNumericPeriod(value, 2) && parseNum(value) <= 99.99)
                          updateRow(i, { stationUm: value });
                      }}
                      onBlur={(_, { value }) => {
                        if (value) {
                          const n = Math.min(parseNum(value), 99.99);
                          updateRow(i, { stationUm: fmt2(n) });
                        }
                      }}
                    />
                  </td>
                ))}
              </tr>

              {/* ── WHOLESALE POSTED PRICE ── */}
              <tr>
                <td rowSpan={4} className={styles.groupCell}>
                  {locale.wholesalePostedPrice}
                </td>
                <td className={styles.fieldCell}>{locale.netWpp}</td>
                {productRows.map((row) => (
                  <td
                    key={row.productCode}
                    style={{ backgroundColor: "#f6f6f6", paddingLeft: "27px" }}
                  >
                    <Text className={styles.computed}>
                      {row.depot ? fmt4(computeWpp(row, subsidyRows)) : "0.0000"}
                    </Text>
                  </td>
                ))}
              </tr>
              <tr>
                <td className={styles.fieldCell}>{locale.wppAdjustment}</td>
                {productRows.map((row, i) => (
                  <td key={row.productCode}>
                    <TextField
                      className={styles.numInput}
                      value={row.wppAdjustment}
                      placeholder="0.0000"
                      disabled={!row.depot || row.wppAdjustmentDisabled}
                      onChange={(_, { value }) => {
                        const n = parseFloat(value) || 0;
                        if (isNumericPeriodMinus(value, 4) && n >= -999.9999 && n <= 999.9999)
                          updateRow(i, { wppAdjustment: value });
                      }}
                      onBlur={(_, { value }) => {
                        if (value) {
                          const n = Math.min(Math.max(parseFloat(value) || 0, -999.9999), 999.9999);
                          updateRow(i, { wppAdjustment: fmt4(n) });
                        }
                      }}
                    />
                  </td>
                ))}
              </tr>
              <tr>
                <td className={styles.fieldCell}>{locale.orderingWpp}</td>
                {productRows.map((row) => {
                  const finalWpp = computeWpp(row, subsidyRows) + parseNum(row.wppAdjustment);
                  return (
                    <td
                      key={row.productCode}
                      style={{ backgroundColor: "#f6f6f6", paddingLeft: "27px" }}
                    >
                      <Text className={styles.computed}>
                        {row.depot ? fmt4(finalWpp) : "0.0000"}
                      </Text>
                    </td>
                  );
                })}
              </tr>
              <tr>
                <td className={styles.fieldCell}>{locale.previousFinalWpp}</td>
                {productRows.map((row, i) => (
                  <td
                    key={row.productCode}
                    style={{
                      backgroundColor:
                        !row.depot || row.previousFinalWppDisabled ? "#f6f6f6" : undefined,
                    }}
                  >
                    {row.previousFinalWppDisabled ? (
                      <Text className={styles.computed} style={{ paddingLeft: "13px" }}>
                        {row.previousFinalWpp || "0.0000"}
                      </Text>
                    ) : (
                      <TextField
                        className={styles.numInput}
                        value={row.previousFinalWpp}
                        placeholder="0.0000"
                        disabled={!row.depot}
                        onChange={(_, { value }) => {
                          if (isNumericPeriod(value, 4) && parseNum(value) <= 9999.9999)
                            updateRow(i, { previousFinalWpp: value });
                        }}
                        onBlur={(_, { value }) => {
                          if (value) {
                            const n = Math.min(parseNum(value), 9999.9999);
                            updateRow(i, { previousFinalWpp: fmt4(n) });
                          }
                        }}
                      />
                    )}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>

        {saveAttempted && !hasValidProductRows && (
          <Text className={styles.tableError}>
            {productRows.every((r) => !r.depot)
              ? "Please select a depot for at least one product."
              : productRows.some(
                    (r) =>
                      !r.depot &&
                      (r.freight || r.pumpPrice || r.previousPumpPrice || r.stationUm),
                  )
                ? "Please select a depot for all products with pricing data."
                : "Please fill in all required fields for selected products."}
          </Text>
        )}
      </div>
    </div>
  );
};

export default PriceBuildUpEditModule;
